import { Page } from "../layout/page";
import { Accordion, Title, Tabs, Text } from "@mantine/core";
import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuthUser } from "react-auth-kit";
import { useLocation } from "react-router-dom";
import { REPORT_CATEGORIES } from "../data/reportCategories";

const hasLinkAccess = (userRole, linkId) =>
  (userRole?.links ?? []).some(
    (entry) => entry.link === linkId && (entry.accessRight ?? 0) > 0
  );

const hasCategoryAccess = (userRole, category) => {
  const linkIds =
    category.linkIds || (category.linkId ? [category.linkId] : null);
  if (!linkIds?.length) return true;
  return linkIds.some((linkId) => hasLinkAccess(userRole, linkId));
};

const Report = ({}) => {
  const { t } = useTranslation();
  const auth = useAuthUser();
  const { userRole } = auth();
  const location = useLocation();

  const categories = useMemo(
    () =>
      REPORT_CATEGORIES.filter((category) => hasCategoryAccess(userRole, category)),
    [userRole]
  );

  const tabFromQuery = useMemo(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    return categories.some((category) => category.id === tab) ? tab : null;
  }, [categories, location.search]);

  const [activeTab, setActiveTab] = useState(
    () => tabFromQuery || categories[0]?.id || "orders"
  );

  useEffect(() => {
    if (tabFromQuery) {
      setActiveTab(tabFromQuery);
    }
  }, [tabFromQuery]);

  const renderReportAccordion = (reports) => (
    <Accordion
      variant="separated"
      radius="xs"
      defaultValue="customization"
      mt={"md"}
    >
      {reports.map((r) => (
        <Accordion.Item key={r.title} value={r.title}>
          <Accordion.Control>
            <Title order={5}>{t(r.title)}</Title>
            {r.subtitle && (
              <Text size="sm" color="dimmed">
                {t(r.subtitle)}
              </Text>
            )}
          </Accordion.Control>
          <Accordion.Panel>
            <r.reportComponent />
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );

  return (
    <Page title={t("Report")}>
      <Tabs variant="default" value={activeTab} onTabChange={setActiveTab}>
        <Tabs.List>
          {categories.map((category) => (
            <Tabs.Tab key={category.id} value={category.id}>
              {t(category.label)}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {categories.map((category) => (
          <Tabs.Panel key={category.id} value={category.id} pt="xl">
            {renderReportAccordion(category.reports)}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Page>
  );
};

export default Report;
